import { ExternalTokenizer, type InputStream } from '@lezer/lr';
import { orderBy } from 'lodash-es';

export const readStreamToEnd = (input: InputStream): string => {
  let result = '';
  for (let i = 0; ; i++) {
    const ch = input.peek(i);
    if (ch === -1) {
      break;
    }
    result += String.fromCharCode(ch);
  }
  return result;
};

export const VariableName = 1,
  Literal = 2,
  Program = 3,
  UserInputSequence = 4,
  UserInputSequenceItem = 5;

export const variableWithIndexerRegex =
  /(@@|\$\$|&&)[a-zA-Z_][a-zA-Z0-9\-_]*;?(\["[^"]*"]|\[\d+]|\.\.)+/;
export const localVariableRegex = /(@@|\$\$|&&)[a-zA-Z0-9\-_]+;/;
export const columnReferenceRegex = /{colId:[0-9a-fA-F_]+}(\["[^"]*"]|\[\d+]|\.\.)*/;
export const tagIdRegex = /tagId:[0-9a-fA-F]{24}/;

export const temporaryFunctionRegex = /_#_.+_#_/;

const dateRegex = /\d{4}-\d{2}-\d{2}/;
const dateTimeRegex = /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d+Z/;

export const possibleVariablePatterns = [
  variableWithIndexerRegex,
  localVariableRegex,
  columnReferenceRegex,
  tagIdRegex,
  temporaryFunctionRegex,
  dateTimeRegex,
  dateRegex,
];

// Underscores may only join name segments (ARRAY_INSERT), not start or end the name.
export const functionNameRegex = /^[A-Z][A-Z0-9]*(?:_[A-Z0-9]+)*\(/;

export const FunctionNameTokenizer = new ExternalTokenizer((input: InputStream) => {
  const code = readStreamToEnd(input);
  const match = functionNameRegex.exec(code);
  if (!match) {
    return;
  }
  const functionName = match[0].slice(0, -1);
  input.advance(functionName.length);
  const FunctionName = 1;
  input.acceptToken(FunctionName);
});

export const SimpleDialectTokenizer = new ExternalTokenizer(
  (input: InputStream) => {
    const code = readStreamToEnd(input);

    const matches = possibleVariablePatterns.map((pattern) => pattern.exec(code));
    const sortedMatches = orderBy(
      matches,
      [
        (match): number | undefined => match?.index,
        (match): number | undefined => match?.[0].length,
      ],
      ['asc', 'desc'],
    );

    const [match] = sortedMatches;

    if (!match) {
      if (code.length > 0) {
        input.advance(code.length);
        input.acceptToken(Literal);
      }
      return;
    }
    if (!match) {
      return;
    }
    if (match.index) {
      input.advance(match.index);
      input.acceptToken(Literal);
      return;
    }
    input.advance(match[0].length);
    input.acceptToken(VariableName);
  },
  { extend: true },
);
